// =============================================================
// BTR Companion — App shell & routing
// Direction artistique « Companion » · accent Demacia figé, mode sombre.
// Données réelles via l'API Node (proxy /api) + Data Dragon.
// =============================================================
import React, { useState, useEffect, useCallback } from 'react';
import './companion.css';
import { initDdragon } from './ddragon';
import { NavBar, SiteFooter, Spinner } from './components';
import { HomeScreen, ProfileScreen, MatchScreen } from './screens1';
import { ChampionsScreen, ChampionDetailScreen, BuildScreen } from './screens2';
import { StatusScreen, GuesserScreen, ArenaScreen } from './screens3';
import { ClashScreen } from './screens4';
import { CompanionScreen, LegalScreen, PrivacyScreen, NotFoundScreen } from './screens5';
import { loadRegion, saveRegion } from './format';

const SITE = 'BuildTheRift';
const HOME_TITLE = 'BuildTheRift · Stats, builds et profils League of Legends';

const ROUTES = {
  home:      { label: 'Accueil',   comp: HomeScreen },
  profile:   { label: 'Profil',    comp: ProfileScreen },
  match:     { label: 'Match',     comp: MatchScreen },
  champions: { label: 'Champions', comp: ChampionsScreen },
  champion:  { label: 'Champion',  comp: ChampionDetailScreen },
  build:     { label: 'Builds',    comp: BuildScreen },
  status:    { label: 'Serveurs Riot', comp: StatusScreen },
  guesser:   { label: 'Jeux',      comp: GuesserScreen },
  arena:     { label: 'Arena · Challenge Top 1', comp: ArenaScreen },
  clash:     { label: 'Clash · Scouting', comp: ClashScreen },
  companion: { label: 'Companion desktop', comp: CompanionScreen },
  mentions:  { label: 'Mentions légales', comp: LegalScreen },
  confidentialite: { label: 'Confidentialité', comp: PrivacyScreen },
  notfound:  { label: 'Page introuvable', comp: NotFoundScreen },
};
// Anciens hashs / variantes tolérées
const ALIASES = { legal: 'mentions', privacy: 'confidentialite', builder: 'build', servers: 'status' };

// Hash partageable : "#profile/EUW/Nom/TAG" -> { region, name, tag }
function summonerFromHash() {
  try {
    const parts = decodeURIComponent(window.location.hash.replace('#', '')).split('/');
    if (parts[0] === 'profile' && parts.length >= 4 && parts[2] && parts[3]) {
      return { region: parts[1] || 'EUW', name: parts[2], tag: parts[3] };
    }
  } catch { /* hash mal formé */ }
  return null;
}
const routeFromHash = () => {
  const r = window.location.hash.replace('#', '').split('/')[0] || 'home';
  return ALIASES[r] || r;
};

export default function CompanionApp({ notFound = false }) {
  const [ready, setReady] = useState(false);
  const [route, _setRoute] = useState(() => (notFound ? 'notfound' : routeFromHash()));
  // Sélection courante propagée entre écrans (champion, invocateur, match, recherche brute).
  const [nav, setNav] = useState({ champId: 'Ahri', summoner: summonerFromHash(), matchId: null, query: null });
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('btr-theme') || 'dark'; } catch { return 'dark'; }
  });
  const [region, setRegionState] = useState(loadRegion);
  const setRegion = useCallback((r) => { setRegionState(r); saveRegion(r); }, []);

  useEffect(() => { initDdragon().finally(() => setReady(true)); }, []);

  const toggleTheme = useCallback(() => {
    setTheme((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('btr-theme', next); } catch { /* navigation privée */ }
      return next;
    });
  }, []);

  const setRoute = useCallback((r, payload) => {
    if (payload) setNav((n) => ({ ...n, ...payload }));
    _setRoute(r);
    // Depuis une URL inconnue (404), on revient à la racine avant de poser le hash.
    if (window.location.pathname !== '/' && window.location.pathname !== '/index.html') {
      window.history.replaceState(null, '', '/');
    }
    // Profil avec invocateur : hash profond partageable
    const s = r === 'profile' ? payload?.summoner : null;
    window.location.hash = s
      ? `#profile/${s.region}/${encodeURIComponent(s.name)}/${encodeURIComponent(s.tag)}`
      : '#' + r;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const h = () => {
      _setRoute(routeFromHash());
      const s = summonerFromHash();
      if (s) setNav((n) => ({ ...n, summoner: s, query: null }));
    };
    window.addEventListener('hashchange', h);
    return () => window.removeEventListener('hashchange', h);
  }, []);

  // Titre d'onglet par écran
  useEffect(() => {
    const def = ROUTES[route];
    document.title = route === 'home' ? HOME_TITLE : `${def?.label || 'Page introuvable'} · ${SITE}`;
  }, [route]);

  if (!ready) {
    return (
      <div className="shell" data-accent="demacia" data-mode={theme} data-density="cozy"
           style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner label="Chargement des données du Rift…" />
      </div>
    );
  }

  const known = !!ROUTES[route];
  const RouteComp = known ? ROUTES[route].comp : NotFoundScreen;
  const screenLabel = known ? ROUTES[route].label : 'Page introuvable';

  return (
    <div className="shell" data-accent="demacia" data-mode={theme} data-density="cozy">
      <NavBar route={route} setRoute={setRoute} theme={theme} onToggleTheme={toggleTheme}
              region={region} setRegion={setRegion}/>
      <main className="main" key={route} data-screen-label={`${route} · ${screenLabel}`}>
        <RouteComp setRoute={setRoute} nav={nav} setNav={setNav} region={region}/>
      </main>
      <SiteFooter setRoute={setRoute}/>
    </div>
  );
}
